import { useState, type FormEvent } from 'react'
import { Link, router } from '@inertiajs/react'
import type { AppShellProps } from '@/components/AppShell'
import RegistrationAgendaSessions, { type AgendaDay, type AgendaLayout } from '@/components/RegistrationAgendaSessions'
import RegistrationPageLayout from '@/components/RegistrationPageLayout'
import RegistrationStepIndicator from '@/components/RegistrationStepIndicator'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import type { EventTheme } from '@/lib/registrationFormShared'

interface Props extends Omit<AppShellProps, 'children'> {
  event: EventTheme
  currentLocale: string
  locales: string[]
  eventUser: { token: string | null; firstName: string | null; lastName: string | null }
  days: AgendaDay[]
  selectedIds: number[]
  agendaSelectionRequired: boolean
  agendaLayout: AgendaLayout
  agendaGroupSessions?: boolean
  agendaShowFromToLabels?: boolean
  wizard: {
    currentStep: number
    totalSteps: number
    hasAgendaStep: boolean
  }
  registrationLinkSlug?: string | null
  urls: AppShellProps['urls'] & {
    submit: string
    form: string
    landing: string
  }
  translations: AppShellProps['translations'] & {
    selectSessions: string
    saveAndContinue: string
    day: string
    back: string
    register: string
    stepLanding: string
    stepForm: string
    stepAgenda: string
    stepConfirmation: string
    startTime?: string
    endTime?: string
    allSeatsBooked?: string
  }
}

export default function RegistrationAgenda(props: Props) {
  const {
    event,
    currentLocale,
    locales,
    eventUser,
    days,
    selectedIds,
    agendaSelectionRequired,
    agendaLayout,
    agendaGroupSessions,
    agendaShowFromToLabels,
    wizard,
    urls,
    registrationLinkSlug,
    translations: t,
    ...shell
  } = props
  const [selected, setSelected] = useState<Set<number>>(new Set(selectedIds))
  const [submitting, setSubmitting] = useState(false)

  const toggle = (id: number) => {
    const locked = days.some((day) => day.dayItems.some((item) => item.id === id && (item.soldOut || item.disabled)))
    if (locked) return

    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    const data = new FormData()
    Array.from(selected).forEach((id) => data.append('day_item_ids[]', String(id)))
    router.post(urls.submit, data, {
      forceFormData: true,
      onFinish: () => setSubmitting(false),
    })
  }

  const theme = {
    primaryColor: event.primaryColor,
    regTextColor: event.regTextColor,
    regInputBorderColor: event.regInputBorderColor,
    regCardBgColor: event.regCardBgColor,
  }

  const layoutClass = agendaLayout === 'cards'
    ? 'ev-reg-agenda-layout-cards'
    : agendaLayout === 'timeline'
      ? 'ev-reg-agenda-layout-timeline'
      : agendaLayout === 'table'
        ? 'ev-reg-agenda-layout-table'
        : 'ev-reg-agenda-layout-list'

  return (
    <RegistrationPageLayout
      event={event}
      currentLocale={currentLocale}
      locales={locales}
      urls={urls}
      translations={t}
      pageTitle={`${t.selectSessions} – ${event.name}`}
      pageVariant="agenda"
      registrationLinkSlug={registrationLinkSlug}
      {...shell}
    >
      <div className={`ev-reg-card ev-reg-agenda-card rounded-lg border p-6 ${layoutClass}`}>
        <RegistrationStepIndicator
          wizard={wizard}
          labels={{ stepLanding: t.stepLanding, stepForm: t.stepForm, stepAgenda: t.stepAgenda, stepConfirmation: t.stepConfirmation }}
        />

        <div className="ev-reg-back-link mb-4">
          <Link href={urls.form} className="ev-reg-back-link-anchor text-sm underline">
            ← {t.back}
          </Link>
        </div>

        <h2 className="ev-reg-agenda-title text-xl font-semibold mb-1">{t.selectSessions}</h2>
        <p className="ev-reg-agenda-attendee text-sm mb-6 opacity-70">
          {[eventUser.firstName, eventUser.lastName].filter(Boolean).join(' ')}
        </p>

        <form onSubmit={handleSubmit} className="ev-reg-agenda-form space-y-6">
          <RegistrationAgendaSessions
            days={days}
            selected={selected}
            onToggle={toggle}
            layout={agendaLayout}
            dayLabel={t.day}
            fromLabel={t.startTime || t.fromTime || (currentLocale.startsWith('ar') ? 'من' : 'From')}
            toLabel={t.endTime || t.toTime || (currentLocale.startsWith('ar') ? 'إلى' : 'To')}
            locale={currentLocale || 'en'}
            theme={theme}
            groupSessions={agendaGroupSessions !== false}
            showFromToLabels={agendaShowFromToLabels !== false}
            allSeatsBookedLabel={t.allSeatsBooked || 'All seats booked'}
          />

          <Button
            type="submit"
            disabled={submitting || (agendaSelectionRequired && selected.size === 0)}
            className="ev-reg-button ev-reg-agenda-submit w-full h-11 text-base font-semibold"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t.saveAndContinue}
          </Button>
        </form>
      </div>
    </RegistrationPageLayout>
  )
}
